import { useEffect } from "react";
import clsx from "clsx";

import { useAtom } from "../../app-jotai";
import { authStateAtom, loginDialogStateAtom } from "../../auth/authAtoms";
import {
  getAuthToken,
  getAuthUser,
  clearAuthData,
} from "../../auth/authStorage";

import { verifySession } from "../../auth/authApi";
import { useHandleAppTheme } from "../../useHandleAppTheme";

import { AdminDialog } from "../Admin/AdminDialog";

import { LoginDialog } from "./LoginDialog";

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authState, setAuthState] = useAtom(authStateAtom);
  const [, setLoginDialogState] = useAtom(loginDialogStateAtom);
  const { editorTheme } = useHandleAppTheme();

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const token = getAuthToken();
      const user = getAuthUser();

      if (!token || !user) {
        // No stored session, show login
        setAuthState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
        setLoginDialogState({ isOpen: true, step: "username", username: "" });
        return;
      }

      // Verify stored session with backend
      try {
        const result = await verifySession();

        if (result.valid) {
          setAuthState({
            user: result.user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          // Invalid session, clear and show login
          clearAuthData();
          setAuthState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: "Session expired",
          });
          setLoginDialogState({
            isOpen: true,
            step: "username",
            username: "",
          });
        }
      } catch (error) {
        // Verification failed, clear and show login
        console.error("Session verification failed:", error);
        clearAuthData();
        setAuthState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: "Session verification failed",
        });
        setLoginDialogState({ isOpen: true, step: "username", username: "" });
      }
    };

    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Show loading state while checking authentication
  if (authState.isLoading) {
    return (
      <div
        className={clsx("excalidraw excalidraw-app", `theme--${editorTheme}`)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--color-surface-lowest)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              marginBottom: "16px",
              fontSize: "18px",
              fontWeight: 600,
              color: "var(--color-on-surface)",
            }}
          >
            MjölNote
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "var(--color-gray-60)",
            }}
          >
            Loading...
          </div>
        </div>
      </div>
    );
  }

  // Show login dialog if not authenticated
  if (!authState.isAuthenticated) {
    return (
      <div
        className={clsx("excalidraw excalidraw-app", `theme--${editorTheme}`)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--color-surface-lowest)",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "400px" }}>
          <div
            style={{
              marginBottom: "24px",
              fontSize: "32px",
              fontWeight: 700,
              color: "var(--color-on-surface)",
            }}
          >
            MjölNote
          </div>
          <div
            style={{
              fontSize: "16px",
              color: "var(--color-gray-60)",
              lineHeight: 1.6,
            }}
          >
            Please sign in to continue
          </div>
        </div>
        <LoginDialog />
      </div>
    );
  }

  // User is authenticated, render the app
  return (
    <>
      {children}
      <LoginDialog />
      <AdminDialog />
    </>
  );
};
